import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PAGE_SIZE_OPTIONS,
  PageSizeOption,
} from '../../../../../hooks/usePersistedPageSize';
import DropDownInput from '../../../../inputs/DropDown/DropDownInput';
import {
  onChangeValue,
  Options,
} from '../../../../inputs/DropDown/DropDownList/types';
import { Container, Label } from './styles';

export interface PageSizeSelectorProps {
  value: PageSizeOption;
  onChange: (next: PageSizeOption) => void;
  disabled?: boolean;
  /**
   * Opciones a mostrar en el selector. Default: [25, 50, 100]. Reservado por
   * compatibilidad histórica del API; el caller normalmente lo deja en default.
   */
  options?: readonly PageSizeOption[];
}

/**
 * Tarea #11 — Selector de filas por página (25/50/100). Usa el `DropDownInput`
 * canónico del proyecto (no native `<select>`) para mantener consistencia
 * visual con el resto de selectores de la app y soportar accesibilidad/teclado
 * desde un único componente.
 */
export const PageSizeSelector = ({
  value,
  onChange,
  disabled = false,
  options = PAGE_SIZE_OPTIONS,
}: PageSizeSelectorProps): JSX.Element => {
  const { t } = useTranslation('tables');

  const dropdownOptions: Options = useMemo(
    () => options.map((opt) => ({ value: String(opt), label: String(opt) })),
    [options],
  );

  const handleChange: onChangeValue = (option) => {
    if (!option) return;
    const parsed = Number(option.value) as PageSizeOption;
    if (PAGE_SIZE_OPTIONS.includes(parsed)) {
      onChange(parsed);
    }
  };

  return (
    <Container>
      <Label htmlFor="rois-page-size-selector">{t('pageSize.label')}</Label>
      <DropDownInput
        id="rois-page-size-selector"
        inputSize="XS"
        typeDropDown="Table"
        placeholder={String(value)}
        defaultValue={String(value)}
        selectedOption={String(value)}
        options={dropdownOptions}
        onChangeSelected={handleChange}
        isDisabled={disabled}
        /* El selector vive en el footer de la tabla → si el dropdown abre
           hacia abajo se sale del viewport. Forzamos apertura hacia arriba. */
        placement="top"
      />
    </Container>
  );
};
